import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconButton, Menu, MenuItem, ListItemText, ListItemIcon, Divider, ListSubheader, Badge, CircularProgress } from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import CheckIcon from '@mui/icons-material/Check'
import type { LayoutMode } from '@/theme/breakpoints'
import type { MediaPreloadProgress } from '@/services/mediaPreloader'
import { mediaCachePercent, mediaCacheLabelParams } from '@/utils/mediaCacheProgress'

interface HeaderOverflowMenuProps {
  mode: LayoutMode
  canOpenAdminPanel: boolean
  /** False below `md`: the admin panel is blocked there, so the entry point is not offered. */
  showAdminLink: boolean
  hasDraftPreviewAccess: boolean
  showDraftContent: boolean
  draftToggleDisabled: boolean
  onToggleDraft: () => void
  showContentDiff: boolean
  onToggleDiff: () => void
  isOffline: boolean
  offlineDisabled: boolean
  onToggleOffline: () => void
  languages: { id: number; code: string }[]
  selectedLanguage: string
  languageDisabled: boolean
  onLanguageChange: (code: string) => void
  imageCacheProgress: MediaPreloadProgress | null
  onLogout: () => void
  onOpenFaq: () => void
  onOpenReleaseNotes: () => void
}

/**
 * Header controls that do not fit below `lg`.
 *
 * At `medium` this holds only the links and the diff toggle; at `compact` it also
 * absorbs the offline toggle, draft toggles, language selector and the help menu.
 * All state stays in Header: this component renders handlers, it does not own them.
 *
 * The guided tour is deliberately absent. It is gated to `expanded`, so it can
 * never be launched from here (see RESPONSIVE-DESIGN-PLAN.md section 8.1).
 */
export function HeaderOverflowMenu(props: HeaderOverflowMenuProps) {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)
  const close = () => setAnchorEl(null)
  const runAndClose = (fn: () => void) => () => { close(); fn() }

  const isCompact = props.mode === 'compact'
  const caching = props.imageCacheProgress
  const showCaching = isCompact && caching !== null && caching.total > 0

  return (
    <>
      <IconButton
        className="header-overflow-btn"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-label={t('header.moreOptions')}
        aria-haspopup="menu"
        aria-expanded={open}
        sx={{ color: 'var(--color-white)', '&:hover': { backgroundColor: 'var(--color-on-dark-15)' } }}
      >
        <Badge
          variant="dot"
          color="warning"
          invisible={!(isCompact && props.showDraftContent)}
        >
          <MoreVertIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 240, maxHeight: '80dvh' } } }}
      >
        {showCaching && (
          <MenuItem disabled sx={{ gap: 1 }}>
            <CircularProgress
              size={14}
              thickness={5}
              variant="determinate"
              value={mediaCachePercent(caching)}
            />
            <ListItemText
              primary={t('header.cachingImagesProgress', mediaCacheLabelParams(caching))}
              slotProps={{ primary: { fontSize: 13 } }}
            />
          </MenuItem>
        )}
        {showCaching && <Divider />}

        {isCompact && (
          <MenuItem onClick={runAndClose(props.onToggleOffline)} disabled={props.offlineDisabled}>
            <ListItemText primary={props.isOffline ? t('header.goOnline') : t('header.goOffline')} />
          </MenuItem>
        )}

        {isCompact && props.hasDraftPreviewAccess && (
          <MenuItem onClick={runAndClose(props.onToggleDraft)} disabled={props.draftToggleDisabled}>
            <ListItemText primary={props.showDraftContent ? t('draftPreview.toggleOff') : t('draftPreview.toggleOn')} />
          </MenuItem>
        )}

        {props.hasDraftPreviewAccess && props.showDraftContent && (
          <MenuItem onClick={runAndClose(props.onToggleDiff)}>
            <ListItemText primary={props.showContentDiff ? t('draftPreview.diffToggleOff') : t('draftPreview.diffToggleOn')} />
          </MenuItem>
        )}

        {isCompact && props.languages.length > 0 && <Divider />}
        {isCompact && props.languages.length > 0 && (
          <ListSubheader sx={{ lineHeight: '32px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {t('header.languageSection')}
          </ListSubheader>
        )}
        {isCompact && props.languages.map((lang) => (
          <MenuItem
            key={lang.id}
            selected={lang.code === props.selectedLanguage}
            disabled={props.languageDisabled}
            onClick={runAndClose(() => props.onLanguageChange(lang.code))}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              {lang.code === props.selectedLanguage && <CheckIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText primary={lang.code.toUpperCase()} />
          </MenuItem>
        ))}

        <Divider />
        <ListSubheader sx={{ lineHeight: '32px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {t('header.accountSection')}
        </ListSubheader>
        <MenuItem component="a" href={t('links.dashboard')} onClick={close}>
          <ListItemText primary={t('header.dashboard')} />
        </MenuItem>
        {props.canOpenAdminPanel && props.showAdminLink && (
          <MenuItem component="a" href="/admin" onClick={close}>
            <ListItemText primary={t('header.admin')} />
          </MenuItem>
        )}
        <MenuItem onClick={runAndClose(props.onLogout)}>
          <ListItemText primary={t('header.logOut')} />
        </MenuItem>

        {isCompact && <Divider />}
        {isCompact && (
          <MenuItem component="a" href={t('links.support')} target="_blank" rel="noopener noreferrer" onClick={close}>
            <ListItemText primary={t('help.isacaSupport')} />
          </MenuItem>
        )}
        {isCompact && (
          <MenuItem onClick={runAndClose(props.onOpenFaq)}>
            <ListItemText primary={t('help.faqs')} />
          </MenuItem>
        )}
        {isCompact && (
          <MenuItem onClick={runAndClose(props.onOpenReleaseNotes)}>
            <ListItemText primary={t('help.releaseNotes')} />
          </MenuItem>
        )}
      </Menu>
    </>
  )
}
